// ============================================================
// FILE LOCATION: src/components/TimelineBuilder/types.ts
// ============================================================

/**
 * Timeline Builder Types
 * Data models for clinical timeline capture and generation
 */

// Event type categories
export type TimelineEventType =
  | "SYMPTOM"
  | "DIAGNOSIS"
  | "CONSERVATIVE_THERAPY"
  | "MEDICATION"
  | "PROCEDURE"
  | "IMAGING"
  | "LAB"
  | "CONSULT"
  | "ED_ADMISSION"
  | "FUNCTION_CHANGE"
  | "PRIOR_AUTH"
  | "DENIAL"
  | "FOLLOWUP"
  | "ADVERSE_EVENT"
  | "REQUEST";

// Date precision types
export type DatePrecision =
  | "exact"
  | "month"
  | "year"
  | "relative"
  | "duration"
  | "anchored"
  | "approximate";

// Outcome/response options
export type EventOutcome =
  | "IMPROVED"
  | "NO_CHANGE"
  | "WORSENED"
  | "PARTIAL"
  | "INTOLERABLE"
  | "PENDING"
  | null;

// Evidence flags
export type EvidenceFlag =
  | "NOTE_EXISTS"
  | "IMAGING_REPORT"
  | "LAB_RESULTS"
  | "PT_SUMMARY"
  | "RX_RECORD"
  | "CONSULT_NOTE"
  | "DISCHARGE_SUMMARY"
  | "PRIOR_AUTH_LETTER"
  | "DENIAL_LETTER";

// Function/severity tags
export type FunctionTag =
  | "PAIN_SCALE"
  | "ADL_LIMITS"
  | "WORK_IMPAIRMENT"
  | "MOBILITY_LIMITED"
  | "SAFETY_RISK"
  | "SLEEP_DISRUPTION"
  | "QUALITY_OF_LIFE";

// Case types for suggested event templates
export type CaseType =
  | "IMAGING"
  | "PROCEDURE"
  | "SURGERY"
  | "MEDICATION"
  | "LEVEL_OF_CARE"
  | "DME"
  | "THERAPY"
  | "OTHER";

// Relative date structure
export interface RelativeDate {
  value: number;
  unit: "days" | "weeks" | "months" | "years";
  direction: "ago" | "after";
  anchorEventId?: string;
}

// Timeline Event
export interface TimelineEvent {
  id: string;
  timeline_id?: string;
  event_type: TimelineEventType;
  date_precision: DatePrecision;
  start_date?: string; // ISO date string
  end_date?: string; // ISO date string for ranges
  date_label?: string; // Display label like "Jan 2026" or "~6 weeks ago"
  relative_value?: RelativeDate;
  anchor_ref_event_id?: string;
  title: string;
  details?: string; // Max 240 chars
  outcome?: EventOutcome;
  evidence_flags?: EvidenceFlag[];
  function_tags?: FunctionTag[];
  sort_key?: number; // Computed for ordering
  created_at?: string;
  updated_at?: string;
  // UI state
  isNew?: boolean;
  hasValidationWarning?: boolean;
  validationMessage?: string;
}

// Timeline container
export interface Timeline {
  id: string;
  case_id: string;
  case_type: CaseType;
  anchor_date?: string; // Symptom onset or reference date
  events: TimelineEvent[];
  created_at: string;
  updated_at: string;
}

// Generated timeline outputs
export interface TimelineOutput {
  narrative: string;
  bullets: string[];
  proofPoints: string[];
  generatedAt: string;
  eventsUsed: number;
  warnings?: string[];
}

// Event type metadata for UI
export interface EventTypeOption {
  value: TimelineEventType;
  label: string;
  icon: string;
  category: "clinical" | "treatment" | "diagnostic" | "administrative";
}

// Suggested event template
export interface SuggestedEvent {
  event_type: TimelineEventType;
  title: string;
  details?: string;
  placeholder?: string;
}

// Case data for auto-population
export interface TimelineCaseData {
  tried_treatments?: string[];
  clinical_history?: string;
  diagnoses?: string;
  denial_reason?: string;
  request_type?: string;
  request_details?: string;
  comorbidities?: string;
}

// Props for components
export interface TimelineBuilderProps {
  caseId: string;
  caseType: CaseType;
  caseData?: TimelineCaseData; // Case data for auto-populating events
  initialTimeline?: Timeline;
  onTimelineChange?: (timeline: Timeline) => void;
  onGenerateOutput?: (output: TimelineOutput) => void;
  readOnly?: boolean;
}

export interface TimelineEventCardProps {
  event: TimelineEvent;
  index: number;
  onUpdate: (event: TimelineEvent) => void;
  onDelete: (id: string) => void;
  onDuplicate?: (event: TimelineEvent) => void;
  isFirst?: boolean;
  isLast?: boolean;
  readOnly?: boolean;
}

export interface TimelineOutputProps {
  output: TimelineOutput | null;
  isLoading: boolean;
  caseType?: CaseType;
  onInsertToAppeal?: (section: "narrative" | "bullets" | "proofPoints") => void;
  onCopy?: (text: string) => void;
}

// Event type options with metadata
export const EVENT_TYPE_OPTIONS: EventTypeOption[] = [
  {
    value: "SYMPTOM",
    label: "Symptom Onset",
    icon: "🔴",
    category: "clinical",
  },
  { value: "DIAGNOSIS", label: "Diagnosis", icon: "📋", category: "clinical" },
  {
    value: "CONSERVATIVE_THERAPY",
    label: "Conservative Therapy",
    icon: "🏃",
    category: "treatment",
  },
  {
    value: "MEDICATION",
    label: "Medication Trial",
    icon: "💊",
    category: "treatment",
  },
  { value: "PROCEDURE", label: "Procedure", icon: "🔧", category: "treatment" },
  {
    value: "IMAGING",
    label: "Imaging/Diagnostics",
    icon: "📷",
    category: "diagnostic",
  },
  { value: "LAB", label: "Lab Results", icon: "🧪", category: "diagnostic" },
  {
    value: "CONSULT",
    label: "Specialist Consult",
    icon: "👨‍⚕️",
    category: "clinical",
  },
  {
    value: "ED_ADMISSION",
    label: "ED Visit/Admission",
    icon: "🏥",
    category: "clinical",
  },
  {
    value: "FUNCTION_CHANGE",
    label: "Functional Status Change",
    icon: "📉",
    category: "clinical",
  },
  {
    value: "PRIOR_AUTH",
    label: "Prior Authorization",
    icon: "📝",
    category: "administrative",
  },
  {
    value: "DENIAL",
    label: "Denial Received",
    icon: "❌",
    category: "administrative",
  },
  {
    value: "FOLLOWUP",
    label: "Follow-up/Response",
    icon: "🔄",
    category: "clinical",
  },
  {
    value: "ADVERSE_EVENT",
    label: "Adverse Event",
    icon: "⚠️",
    category: "clinical",
  },
  {
    value: "REQUEST",
    label: "Current Request",
    icon: "🎯",
    category: "administrative",
  },
];

// Outcome options
export const OUTCOME_OPTIONS: { value: EventOutcome; label: string }[] = [
  { value: null, label: "Not applicable" },
  { value: "IMPROVED", label: "Improved" },
  { value: "PARTIAL", label: "Partial response" },
  { value: "NO_CHANGE", label: "No change" },
  { value: "WORSENED", label: "Worsened" },
  { value: "INTOLERABLE", label: "Intolerable side effects" },
  { value: "PENDING", label: "Pending/Ongoing" },
];

// Evidence flag options
export const EVIDENCE_FLAG_OPTIONS: { value: EvidenceFlag; label: string }[] = [
  { value: "NOTE_EXISTS", label: "Clinical note" },
  { value: "IMAGING_REPORT", label: "Imaging report" },
  { value: "LAB_RESULTS", label: "Lab results" },
  { value: "PT_SUMMARY", label: "PT/OT summary" },
  { value: "RX_RECORD", label: "Rx record" },
  { value: "CONSULT_NOTE", label: "Consult note" },
  { value: "DISCHARGE_SUMMARY", label: "Discharge summary" },
  { value: "PRIOR_AUTH_LETTER", label: "Prior auth letter" },
  { value: "DENIAL_LETTER", label: "Denial letter" },
];

// Function tag options
export const FUNCTION_TAG_OPTIONS: { value: FunctionTag; label: string }[] = [
  { value: "PAIN_SCALE", label: "Pain scale" },
  { value: "ADL_LIMITS", label: "ADL limitations" },
  { value: "WORK_IMPAIRMENT", label: "Work impairment" },
  { value: "MOBILITY_LIMITED", label: "Mobility limited" },
  { value: "SAFETY_RISK", label: "Safety risk" },
  { value: "SLEEP_DISRUPTION", label: "Sleep disruption" },
  { value: "QUALITY_OF_LIFE", label: "Quality of life" },
];

// Suggested events by case type
export const SUGGESTED_EVENTS_BY_CASE_TYPE: Record<CaseType, SuggestedEvent[]> =
  {
    IMAGING: [
      {
        event_type: "SYMPTOM",
        title: "Symptom onset",
        placeholder: "Describe primary symptoms",
      },
      {
        event_type: "CONSERVATIVE_THERAPY",
        title: "Conservative therapy",
        placeholder: "PT, medications, rest, etc.",
      },
      {
        event_type: "IMAGING",
        title: "Prior imaging",
        placeholder: "X-ray, prior MRI, etc.",
      },
      {
        event_type: "FUNCTION_CHANGE",
        title: "Functional limitation",
        placeholder: "Impact on daily activities",
      },
      {
        event_type: "REQUEST",
        title: "Requested imaging",
        placeholder: "MRI, CT, etc.",
      },
    ],
    PROCEDURE: [
      {
        event_type: "DIAGNOSIS",
        title: "Diagnosis established",
        placeholder: "Primary diagnosis",
      },
      {
        event_type: "CONSERVATIVE_THERAPY",
        title: "Failed conservative therapy",
        placeholder: "PT, medications, injections",
      },
      {
        event_type: "IMAGING",
        title: "Diagnostic workup",
        placeholder: "Imaging and test results",
      },
      {
        event_type: "FUNCTION_CHANGE",
        title: "Functional/safety impact",
        placeholder: "Limitations and risks",
      },
      {
        event_type: "REQUEST",
        title: "Requested procedure",
        placeholder: "Planned intervention",
      },
    ],
    SURGERY: [
      {
        event_type: "DIAGNOSIS",
        title: "Surgical indication",
        placeholder: "Diagnosis requiring surgery",
      },
      {
        event_type: "CONSERVATIVE_THERAPY",
        title: "Failed conservative management",
        placeholder: "Non-surgical treatments tried",
      },
      {
        event_type: "IMAGING",
        title: "Pre-operative workup",
        placeholder: "Imaging and clearances",
      },
      {
        event_type: "PROCEDURE",
        title: "Prior procedures",
        placeholder: "Previous interventions",
      },
      {
        event_type: "FUNCTION_CHANGE",
        title: "Functional/safety impact",
        placeholder: "Current limitations",
      },
      {
        event_type: "REQUEST",
        title: "Planned surgery",
        placeholder: "Surgical procedure requested",
      },
    ],
    MEDICATION: [
      {
        event_type: "DIAGNOSIS",
        title: "Indication/diagnosis",
        placeholder: "Condition requiring treatment",
      },
      {
        event_type: "MEDICATION",
        title: "Prior medication trial",
        placeholder: "Previous medications tried",
      },
      {
        event_type: "LAB",
        title: "Objective measures",
        placeholder: "Labs, vitals, or scores",
      },
      {
        event_type: "ADVERSE_EVENT",
        title: "Side effects/intolerance",
        placeholder: "If applicable",
      },
      {
        event_type: "REQUEST",
        title: "Requested medication",
        placeholder: "Drug and rationale",
      },
    ],
    LEVEL_OF_CARE: [
      {
        event_type: "SYMPTOM",
        title: "Presenting severity",
        placeholder: "Acute presentation",
      },
      {
        event_type: "CONSERVATIVE_THERAPY",
        title: "Failed outpatient management",
        placeholder: "Prior treatment attempts",
      },
      {
        event_type: "ED_ADMISSION",
        title: "ED visit/hospitalization",
        placeholder: "Admission details",
      },
      {
        event_type: "FUNCTION_CHANGE",
        title: "Risk/monitoring needs",
        placeholder: "Why this level of care",
      },
      {
        event_type: "REQUEST",
        title: "Requested level of care",
        placeholder: "Inpatient, SNF, etc.",
      },
    ],
    DME: [
      {
        event_type: "DIAGNOSIS",
        title: "Functional deficit",
        placeholder: "Underlying condition",
      },
      {
        event_type: "CONSERVATIVE_THERAPY",
        title: "PT/OT history",
        placeholder: "Therapy and training",
      },
      {
        event_type: "FUNCTION_CHANGE",
        title: "Safety risk/falls",
        placeholder: "Risk factors",
      },
      {
        event_type: "PROCEDURE",
        title: "Prior devices",
        placeholder: "Current equipment",
      },
      {
        event_type: "REQUEST",
        title: "Requested DME",
        placeholder: "Equipment and expected benefit",
      },
    ],
    THERAPY: [
      {
        event_type: "DIAGNOSIS",
        title: "Diagnosis/indication",
        placeholder: "Condition requiring therapy",
      },
      {
        event_type: "CONSERVATIVE_THERAPY",
        title: "Prior therapy",
        placeholder: "Previous PT/OT/ST",
      },
      {
        event_type: "FUNCTION_CHANGE",
        title: "Functional baseline",
        placeholder: "Current limitations",
      },
      {
        event_type: "REQUEST",
        title: "Requested therapy",
        placeholder: "Type, frequency, goals",
      },
    ],
    OTHER: [
      {
        event_type: "SYMPTOM",
        title: "Symptom onset",
        placeholder: "Primary complaint",
      },
      {
        event_type: "DIAGNOSIS",
        title: "Diagnosis",
        placeholder: "Working diagnosis",
      },
      {
        event_type: "CONSERVATIVE_THERAPY",
        title: "Treatment history",
        placeholder: "Therapies tried",
      },
      {
        event_type: "REQUEST",
        title: "Current request",
        placeholder: "Service requested",
      },
    ],
  };